import { useEffect, useRef, useState, useCallback, type RefObject } from 'react';
import Hls from 'hls.js';
import type { StreamVariant } from '@/lib/videoTypes';

export interface HlsLevel {
  height: number;
  bitrate: number;
  label: string;
}

interface UseHlsOptions {
  onFatalError?: (reason?: string) => void;
  retryKey?: number;
}

/** CORS proxy URL template */
const CORS_PROXY = 'https://proxy.shakespeare.diy/?url=';

/** Check whether the browser can decode a given codec string via MediaSource. */
function isCodecSupported(codecs: string | undefined): boolean {
  if (!codecs) return true;
  try {
    return MediaSource.isTypeSupported(`video/mp4; codecs="${codecs}"`);
  } catch {
    return true;
  }
}

/**
 * Hook that attaches an HLS (m3u8) stream to a <video> element using hls.js.
 *
 * Master playlists (m3u8 files that reference other m3u8 variant playlists)
 * are fully supported — hls.js resolves and switches between nested playlists
 * natively, and all requests (manifest, sub-playlists, init segments, media
 * segments) are routed through the CORS proxy when cross-origin so chained
 * playlists load even from servers without CORS headers.
 */
export function useHls(
  videoRef: RefObject<HTMLVideoElement | null>,
  variant: StreamVariant | null,
  options?: UseHlsOptions,
) {
  const hlsRef = useRef<Hls | null>(null);
  const [levels, setLevels] = useState<HlsLevel[]>([]);
  const [currentLevel, setCurrentLevel] = useState<number>(-1);

  const optionsRef = useRef(options);
  optionsRef.current = options;
  const retryKey = options?.retryKey ?? 0;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !variant || !variant.isHls) return;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const url = variant.url;

    // Native HLS support (Safari, iOS)
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url;
      setLevels([]);
      setCurrentLevel(-1);
      return;
    }

    if (!Hls.isSupported()) {
      console.error('hls.js is not supported in this browser');
      optionsRef.current?.onFatalError?.('hls.js not supported');
      return;
    }

    const hls = new Hls({
      startLevel: -1,
      maxBufferLength: 30,
      maxMaxBufferLength: 60,
      lowLatencyMode: false,
      // Route all cross-origin requests through the CORS proxy. hls.js
      // resolves relative URLs in (sub-)playlists against the original
      // manifest URL, so nested m3u8 playlists and their segments are
      // rewritten here as absolute proxied URLs.
      fetchSetup: (context, init) => {
        const reqUrl = context.url;
        if (!reqUrl.startsWith(window.location.origin)) {
          return new Request(CORS_PROXY + encodeURIComponent(reqUrl), init);
        }
        return new Request(reqUrl, init);
      },
    });

    hlsRef.current = hls;

    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      hls.loadSource(url);
    });

    hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
      const parsedLevels: HlsLevel[] = data.levels.map((level, i) => ({
        height: level.height || 0,
        bitrate: level.bitrate || 0,
        label: level.height
          ? `${level.height}p`
          : level.bitrate
            ? `${(level.bitrate / 1_000_000).toFixed(1)} Mbps`
            : `Level ${i + 1}`,
      }));

      setLevels([
        { height: 0, bitrate: 0, label: 'Auto' },
        ...parsedLevels,
      ]);
      setCurrentLevel(-1);

      // Codec fallback: if the auto-selected level uses an unsupported codec,
      // switch to the highest supported level.
      const autoLevel = hls.currentLevel >= 0 ? hls.currentLevel : 0;
      const autoLevelData = data.levels[autoLevel];
      if (autoLevelData) {
        const autoCodecs = autoLevelData.videoCodec || autoLevelData.codecs;
        if (!isCodecSupported(autoCodecs)) {
          for (let i = data.levels.length - 1; i >= 0; i--) {
            const levelCodecs = data.levels[i].videoCodec || data.levels[i].codecs;
            if (isCodecSupported(levelCodecs)) {
              hls.currentLevel = i;
              break;
            }
          }
        }
      }

      video.play().catch(() => {});
    });

    hls.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
      setCurrentLevel((prev) => (prev === -1 ? -1 : data.level));
    });

    let networkRetries = 0;
    let mediaRetries = 0;
    const MAX_RETRIES = 3;

    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal) return;

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          networkRetries++;
          if (networkRetries <= MAX_RETRIES) {
            hls.startLoad();
          } else {
            hls.destroy();
            hlsRef.current = null;
            optionsRef.current?.onFatalError?.(
              'Network error — could not fetch the HLS stream.',
            );
          }
          break;

        case Hls.ErrorTypes.MEDIA_ERROR: {
          mediaRetries++;
          const currentLvl = hls.currentLevel;
          if (currentLvl > 0) {
            hls.currentLevel = currentLvl - 1;
            hls.recoverMediaError();
            return;
          }
          if (mediaRetries <= MAX_RETRIES) {
            hls.recoverMediaError();
          } else {
            hls.destroy();
            hlsRef.current = null;
            optionsRef.current?.onFatalError?.(
              'Media error — the video codec may not be supported by your browser.',
            );
          }
          break;
        }

        default:
          hls.destroy();
          hlsRef.current = null;
          optionsRef.current?.onFatalError?.(
            'Failed to load the HLS stream. The manifest may be malformed or the server unreachable.',
          );
          break;
      }
    });

    hls.attachMedia(video);

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      setLevels([]);
      setCurrentLevel(-1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant?.url, variant?.isHls, retryKey]);

  const setLevel = useCallback((level: number) => {
    setCurrentLevel(level);
    if (hlsRef.current) {
      hlsRef.current.currentLevel = level;
    }
  }, []);

  return { levels, currentLevel, setLevel };
}
